import { useEffect, useRef, useState } from "react"

export const AGENT_NETWORK_AUTO_RETRY_SEC = 10

type UseAgentNetworkAutoRetryArgs = {
  active: boolean
  retryText: string
  lockKey: string
  onRetry: () => void | Promise<unknown>
}

async function withNetworkRetryLock(lockKey: string, run: () => void | Promise<unknown>): Promise<void> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined
  if (!locks?.request) {
    await run()
    return
  }

  await locks.request(lockKey, { ifAvailable: true }, async (lock) => {
    if (!lock) return
    await run()
  })
}

/**
 * Live agent `network` failure: count down, then one tab (Web Lock) resends the last prompt.
 * Inactive on stale failed runs — the caller must pass `active` only for in-session failures.
 */
export function useAgentNetworkAutoRetry({
  active,
  retryText,
  lockKey,
  onRetry,
}: UseAgentNetworkAutoRetryArgs): number | null {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const onRetryRef = useRef(onRetry)
  const retryTextRef = useRef(retryText)
  onRetryRef.current = onRetry
  retryTextRef.current = retryText

  useEffect(() => {
    if (!active) {
      setSecondsLeft(null)
      return
    }

    let remaining = AGENT_NETWORK_AUTO_RETRY_SEC
    let fired = false
    setSecondsLeft(remaining)

    const fire = () => {
      if (fired) return
      fired = true
      if (!retryTextRef.current.trim()) return
      void withNetworkRetryLock(lockKey, () => onRetryRef.current())
    }

    const interval = window.setInterval(() => {
      remaining -= 1
      if (remaining <= 0) {
        window.clearInterval(interval)
        setSecondsLeft(0)
        fire()
        return
      }
      setSecondsLeft(remaining)
    }, 1000)

    return () => {
      window.clearInterval(interval)
    }
  }, [active, lockKey])

  return active ? secondsLeft : null
}
